import React, { useState } from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  Typography,
  Button,
  Stack,
  Box,
  Select,
  Option,
  FormControl,
  FormLabel,
} from '@mui/joy';
import { Plus as Add } from '@phosphor-icons/react';

interface AddWorkflowItemModalProps {
  open: boolean;
  onClose: () => void;
  onAdd: (itemType: 'trigger' | 'action' | 'modifier', specificType: string) => void;
  allowedTypes?: ('trigger' | 'action' | 'modifier')[];
}

// Enabled triggers: new_contact_added, form_submitted, opportunity_won, job_complete, opportunity_tag, job_tag,
// contact_tag, task_added, task_completed, payment_received, payment_overdue, estimate_requested
const TRIGGER_TYPES = [
  { value: 'opportunity_stage_changed', label: 'Opportunity Stage Changed', disabled: false },
  { value: 'opportunity_stage_stalled', label: 'Opportunity Stalled in Stage', disabled: false },
  { value: 'job_stage_changed', label: 'Job Stage Changed', disabled: false },
  { value: 'job_stage_stalled', label: 'Job Stalled in Stage', disabled: false },
  { value: 'new_contact_added', label: 'New Contact Added', disabled: false },
  { value: 'form_submitted', label: 'Form Submitted', disabled: false },
  { value: 'opportunity_won', label: 'Opportunity Won', disabled: false },
  { value: 'job_complete', label: 'Job Complete', disabled: false },
  { value: 'opportunity_tag', label: 'Opportunity Tag', disabled: false },
  { value: 'job_tag', label: 'Job Tag', disabled: false },
  { value: 'contact_tag', label: 'Contact Tag', disabled: false },
  { value: 'task_added', label: 'Task Added', disabled: false },
  { value: 'task_completed', label: 'Task Completed', disabled: false },
  { value: 'payment_received', label: 'Payment Received', disabled: false },
  { value: 'payment_overdue', label: 'Payment Overdue', disabled: false },
  { value: 'estimate_requested', label: 'Estimate Requested', disabled: false },
  { value: 'weather_report_generated', label: 'Weather Report Generated', disabled: false },
];

// Enabled actions: create_task, send_email, create_notification, send_sms, assign_to_user, create_contact,
// add_tag, remove_tag, add_note, modify_opportunity_confidence, create_opportunity, send_review_request
const ACTION_TYPES = [
  { value: 'create_task', label: 'Create Task', disabled: false },
  { value: 'send_email', label: 'Send Email', disabled: false },
  { value: 'create_notification', label: 'Create Notification', disabled: false },
  { value: 'send_sms', label: 'Send SMS', disabled: false },
  { value: 'assign_to_user', label: 'Assign to User', disabled: false },
  { value: 'create_contact', label: 'Create Contact', disabled: false },
  { value: 'add_tag', label: 'Add Tag', disabled: false },
  { value: 'remove_tag', label: 'Remove Tag', disabled: false },
  { value: 'add_note', label: 'Add Note', disabled: false },
  { value: 'modify_opportunity_confidence', label: 'Modify Opportunity Confidence', disabled: false },
  { value: 'create_opportunity', label: 'Create Opportunity', disabled: false },
  { value: 'send_review_request', label: 'Send Review Request', disabled: false },
];

const MODIFIER_TYPES = [
  { value: 'wait', label: 'Wait' },
  { value: 'end', label: 'End Workflow' },
  { value: 'if_else', label: 'If/Else' },
];

const AddWorkflowItemModal: React.FC<AddWorkflowItemModalProps> = ({
  open,
  onClose,
  onAdd,
  allowedTypes = ['trigger', 'action', 'modifier'],
}) => {
  const [itemType, setItemType] = useState<'trigger' | 'action' | 'modifier' | null>(null);
  const [specificType, setSpecificType] = useState<string>('');

  const handleReset = () => {
    setItemType(null);
    setSpecificType('');
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleAdd = () => {
    if (itemType && specificType) {
      onAdd(itemType, specificType);
      handleClose();
    }
  };

  const getTypeOptions = () => {
    switch (itemType) {
      case 'trigger':
        return TRIGGER_TYPES;
      case 'action':
        return ACTION_TYPES;
      case 'modifier':
        return MODIFIER_TYPES;
      default:
        return [];
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog sx={{ width: 'min(500px, calc(100vw - 32px))', minWidth: 0 }}>
        <ModalClose />
        <Typography level="h4" sx={{ mb: 2 }}>
          Add Workflow Item
        </Typography>

        <Stack spacing={3}>
          {/* Step 1: Select item type (trigger/action/modifier) */}
          {!itemType && (
            <Box>
              <Typography level="body-md" sx={{ mb: 2 }}>
                What type of item would you like to add?
              </Typography>
              <Stack spacing={1}>
                {allowedTypes.includes('trigger') && (
                  <Button
                    variant="outlined"
                    color="success"
                    onClick={() => setItemType('trigger')}
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography level="title-md">Trigger</Typography>
                      <Typography level="body-sm">
                        An event that starts the workflow
                      </Typography>
                    </Box>
                  </Button>
                )}
                {allowedTypes.includes('action') && (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => setItemType('action')}
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography level="title-md">Action</Typography>
                      <Typography level="body-sm">
                        Something that happens in the workflow
                      </Typography>
                    </Box>
                  </Button>
                )}
                {allowedTypes.includes('modifier') && (
                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={() => setItemType('modifier')}
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography level="title-md">Modifier</Typography>
                      <Typography level="body-sm">
                        Controls workflow flow (Wait, If/Else, End)
                      </Typography>
                    </Box>
                  </Button>
                )}
              </Stack>
            </Box>
          )}

          {/* Step 2: Select specific type */}
          {itemType && (
            <FormControl>
              <FormLabel>
                Select {itemType.charAt(0).toUpperCase() + itemType.slice(1)} Type
              </FormLabel>
              <Select
                value={specificType}
                onChange={(_, value) => setSpecificType(value || '')}
                placeholder={`Choose a ${itemType}...`}
              >
                {getTypeOptions().map((option) => {
                  const isDisabled = 'disabled' in option ? Boolean(option.disabled) : false;
                  return (
                    <Option
                      key={option.value}
                      value={option.value}
                      disabled={isDisabled}
                      sx={isDisabled ? { color: 'text.tertiary' } : {}}
                    >
                      {option.label}
                    </Option>
                  );
                })}
              </Select>
            </FormControl>
          )}

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            {itemType && (
              <Button variant="plain" onClick={handleReset}>
                Back
              </Button>
            )}
            <Button variant="outlined" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              startDecorator={<Add />}
              onClick={handleAdd}
              disabled={!itemType || !specificType}
            >
              Add {itemType || 'Item'}
            </Button>
          </Box>
        </Stack>
      </ModalDialog>
    </Modal>
  );
};

export default AddWorkflowItemModal;
